import { useEffect, useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { PlatformUser } from "@/hooks/useUserFilters";

interface Props {
  user: PlatformUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const fullName = (u: PlatformUser): string => {
  const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
  return name || u.email || "Unnamed user";
};

const initials = (u: PlatformUser): string => {
  const f = (u.first_name ?? "").trim();
  const l = (u.last_name ?? "").trim();
  if (f || l) return `${f.charAt(0)}${l.charAt(0)}`.toUpperCase();
  return (u.email ?? "?").charAt(0).toUpperCase();
};

const RoleBadge = ({ role }: { role: string | null | undefined }) => {
  const r = (role ?? "").toLowerCase();
  if (r === "superuser") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-500/15 text-purple-300 border border-purple-500/30">
        Superuser
      </span>
    );
  }
  if (r === "admin") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/15 text-blue-300 border border-blue-500/30">
        Admin
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700/40 text-slate-300 border border-slate-600/40">
      Member
    </span>
  );
};

const StatusBadge = ({ status }: { status: string | null | undefined }) => {
  const s = (status ?? "").toLowerCase();
  if (s === "active") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
        Active
      </span>
    );
  }
  if (s === "suspended") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">
        Suspended
      </span>
    );
  }
  if (s === "pending") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/15 text-blue-300 border border-blue-500/30">
        Pending
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-700/40 text-slate-400 border border-slate-600/40">
      Deactivated
    </span>
  );
};

export default function UserProfileSheet({ user, open, onOpenChange }: Props) {
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [sendingReset, setSendingReset] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [emailUpdateError, setEmailUpdateError] = useState<string | null>(null);
  const [emailConfirmText, setEmailConfirmText] = useState('');

  useEffect(() => {
    setResetSent(false);
    setNewUserEmail('');
    setEmailUpdateError(null);
    setEmailConfirmText('');
    if (!user?.id) {
      setProfilePhotoUrl(null);
      return;
    }
    supabase
      .from("profiles")
      .select("profile_photo_url")
      .eq("id", user.id)
      .single()
      .then(({ data }) => setProfilePhotoUrl(data?.profile_photo_url ?? null));
  }, [user?.id]);

  useEffect(() => {
    if (!open) {
      setResetSent(false);
      setNewUserEmail('');
      setEmailUpdateError(null);
      setEmailConfirmText('');
    }
  }, [open]);

  const handleUpdateEmail = async () => {
    if (!user?.id || !newUserEmail.trim()) return;
    
    if (emailConfirmText !== 'CONFIRM EMAIL CHANGE') {
      setEmailUpdateError('Please type CONFIRM EMAIL CHANGE exactly to proceed.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUserEmail.trim())) {
      setEmailUpdateError('Please enter a valid email address.');
      return;
    }

    setUpdatingEmail(true);
    setEmailUpdateError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const response = await fetch(
        'https://ptdjtgonyxilanijzugt.supabase.co/functions/v1/admin-update-user-email',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            userId: user.id,
            newEmail: newUserEmail.trim(),
            oldEmail: user.email,
          }),
        }
      );

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to update email');

      toast.success(`Email updated to ${newUserEmail.trim()}`);
      setNewUserEmail('');
      setEmailConfirmText('');
    } catch (err) {
      setEmailUpdateError(err instanceof Error ? err.message : 'Failed to update email.');
    } finally {
      setUpdatingEmail(false);
    }
  };

  const handleSendPasswordReset = async () => {
    if (!user?.email) return;
    setSendingReset(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
        redirectTo: 'https://app.doyencegroup.com/reset-password',
      });
      if (error) throw error;
      setResetSent(true);
      toast.success(`Password reset email sent to ${user.email}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send reset email.');
    } finally {
      setSendingReset(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-slate-900 border-slate-700 text-slate-100 sm:max-w-md w-full overflow-y-auto"
      >
        {user && (
          <div className="space-y-6">
            <SheetHeader>
              <SheetTitle className="text-white">{fullName(user)}</SheetTitle>
              <SheetDescription className="text-slate-400">
                {user.email ?? "—"}
              </SheetDescription>
            </SheetHeader>

            <div className="bg-slate-800 border border-slate-700 rounded-lg p-4">
              <div className="flex items-start gap-4">
                {profilePhotoUrl ? (
                  <img
                    src={profilePhotoUrl}
                    alt={fullName(user)}
                    className="rounded-full h-16 w-16 object-cover"
                  />
                ) : (
                  <div className="rounded-full h-16 w-16 bg-slate-600 flex items-center justify-center text-white font-semibold">
                    {initials(user)}
                  </div>
                )}
                <div className="flex flex-col gap-1 min-w-0">
                  <div className="text-white font-semibold truncate">{fullName(user)}</div>
                  <div className="text-slate-400 text-sm truncate">{user.email ?? "—"}</div>
                  <div className="flex items-center gap-2 mt-1">
                    <RoleBadge role={user.admin_role} />
                    <StatusBadge status={user.status} />
                  </div>
                </div>
              </div>
            </div>

            <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-3 text-sm">
              <dt className="text-slate-500">Company</dt>
              <dd className="text-slate-200">{user.company_name ?? "—"}</dd>

              <dt className="text-slate-500">Position</dt>
              <dd className="text-slate-200">{user.company_position ?? "—"}</dd>

              <dt className="text-slate-500">Phone</dt>
              <dd className="text-slate-200">{user.personal_phone_number ?? "—"}</dd>

              <dt className="text-slate-500">Profile complete</dt>
              <dd className={user.profile_completed ? "text-emerald-400" : "text-amber-400"}>
                {user.profile_completed ? "Yes" : "No"}
              </dd>

              <dt className="text-slate-500">Joined</dt>
              <dd className="text-slate-200">{formatDate(user.joined_at)}</dd>

              <dt className="text-slate-500">Member status</dt>
              <dd className="text-slate-200">{user.member_status ?? "—"}</dd>

              {user.suspended_by_company === true && (
                <>
                  <dt className="text-slate-500">Suspended by company</dt>
                  <dd className="text-amber-400">Yes</dd>
                </>
              )}
            </dl>

            {user?.email && user?.status !== 'deactivated' && (
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 space-y-3">
                <div>
                  <h3 className="text-white font-semibold text-sm">Account Access</h3>
                  <p className="text-slate-400 text-xs mt-1">
                    Send the user a link to reset their password via email.
                  </p>
                </div>
                <Button
                  onClick={handleSendPasswordReset}
                  disabled={sendingReset || resetSent}
                  className="w-full"
                >
                  {resetSent ? 'Reset email sent' : sendingReset ? 'Sending…' : 'Send password reset email'}
                </Button>
              </div>
            )}

            {user?.status !== 'deactivated' && (
              <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 space-y-3">
                <div>
                  <h3 className="text-white font-semibold text-sm">Update Email Address</h3>
                  <p className="text-slate-400 text-xs mt-1">
                    Directly updates this user's email address. This action takes effect immediately with no confirmation required from the user. Notifications will be sent to both the old and new email addresses.
                  </p>
                </div>
                <div className="space-y-2">
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => { setNewUserEmail(e.target.value); setEmailUpdateError(null); }}
                    placeholder="New email address"
                    className="w-full bg-slate-950 border border-slate-700 text-slate-100 rounded-md px-3 py-2 text-sm placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                  />
                  <p className="text-xs text-slate-500">
                    Type <span className="text-amber-400 font-mono">CONFIRM EMAIL CHANGE</span> below to proceed.
                  </p>
                  <input
                    type="text"
                    value={emailConfirmText}
                    onChange={(e) => { setEmailConfirmText(e.target.value); setEmailUpdateError(null); }}
                    placeholder="CONFIRM EMAIL CHANGE"
                    className="w-full bg-slate-950 border border-slate-700 text-slate-100 rounded-md px-3 py-2 text-sm placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-500 font-mono"
                  />
                  {emailUpdateError && (
                    <p className="text-xs text-red-400">{emailUpdateError}</p>
                  )}
                  <Button
                    onClick={handleUpdateEmail}
                    disabled={updatingEmail || !newUserEmail.trim() || emailConfirmText !== 'CONFIRM EMAIL CHANGE'}
                    variant="outline"
                    size="sm"
                    className="w-full border-slate-700 bg-slate-900 hover:bg-slate-700 text-slate-200 disabled:opacity-50"
                  >
                    {updatingEmail ? 'Updating…' : 'Update email address'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
